import { defineModule } from '@nexus/core';
import { tenderMarked, tenderStatusChanged } from './events';

const READ = 'tenders.tender.read';
const WRITE = 'tenders.tender.write';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'tenders',
  name: 'tenders.name',
  description: 'tenders.description',
  icon: 'landmark',
  // Projects created from a tender, with the buyer as client from the customers.
  dependsOn: ['projects', 'crm'],
  permissions: [
    { key: READ },
    { key: WRITE },
    { key: 'tenders.tender.comment' },
    { key: 'tenders.tender.remind' },
    { key: 'tenders.tender.tasks' },
    { key: 'tenders.tender.summarize' },
    { key: 'tenders.evidence.read' },
    { key: 'tenders.evidence.write' },
    { key: 'tenders.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'reader',
      name: 'tenders.roles.reader',
      // The whole company sees the tenders and what is being worked on (Peter, 19 October 2026).
      everyone: true,
      grants: [
        { permission: READ, scope: null },
        { permission: 'tenders.tender.comment', scope: null },
        // Everyone can get a task, e.g. a reference from another department.
        { permission: 'tenders.tender.tasks', scope: null },
        { permission: 'tenders.evidence.read', scope: null },
      ],
    },
    {
      key: 'editor',
      name: 'tenders.roles.editor',
      grants: [
        { permission: 'tenders.tender.*', scope: null },
        { permission: 'tenders.evidence.*', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'tenders.roles.manager',
      grants: [
        { permission: 'tenders.tender.*', scope: null },
        { permission: 'tenders.evidence.*', scope: null },
        { permission: 'tenders.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    { label: 'tenders.nav.tenders', path: '/tenders/tenders', icon: 'landmark', permission: READ },
    { label: 'tenders.nav.mine', path: '/tenders/mine', icon: 'briefcase', permission: READ },
    {
      label: 'tenders.nav.evidence',
      path: '/tenders/evidence',
      icon: 'badge-check',
      permission: 'tenders.evidence.read',
    },
    {
      label: 'tenders.nav.sources',
      path: '/tenders/sources',
      icon: 'database',
      permission: 'tenders.settings.manage',
    },
  ],
  settings: [
    { key: 'tenders.sourceOev', level: 'company', type: 'boolean', default: true },
    { key: 'tenders.sourceNrw', level: 'company', type: 'boolean', default: true },
    { key: 'tenders.sourceTed', level: 'company', type: 'boolean', default: true },
    { key: 'tenders.sourceBerlin', level: 'company', type: 'boolean', default: true },
    { key: 'tenders.sourceVkberlin', level: 'company', type: 'boolean', default: true },
    { key: 'tenders.sourceBund', level: 'company', type: 'boolean', default: true },
    {
      // Days between the checks for an answer of the buyer after the submission.
      key: 'tenders.feedbackIntervalDays',
      level: 'company',
      type: 'number',
      default: 7,
      min: 1,
      max: 90,
      integer: true,
    },
  ],
  // Only tenders in work; the stock of thousands of notices has its own search on the overview.
  search: [{ entity: 'tenders.tender', permission: READ }],
  activity: [
    {
      entity: 'tenders.tender',
      read: READ,
      comment: 'tenders.tender.comment',
      remind: 'tenders.tender.remind',
      tasks: 'tenders.tender.tasks',
      path: '/tenders/tenders/$id',
    },
  ],
  files: [
    // Tender documents, e.g. the ZIP of the portal, which the second stage of the summary reads.
    {
      entity: 'tenders.tender',
      read: READ,
      write: WRITE,
      kinds: ['pdf', 'image', 'office', 'text', 'archive'],
      maxBytes: 100_000_000,
    },
    // Filled-in forms and documents for one point of the checklist.
    { entity: 'tenders.item', read: READ, write: WRITE },
    { entity: 'tenders.evidence', read: 'tenders.evidence.read', write: 'tenders.evidence.write' },
  ],
  // Tender documents in a folder per tender, in SharePoint where the company has connected it.
  documentStore: [{ entity: 'tenders.tender', folder: 'Ausschreibungen' }],
  views: [{ list: 'tenders.tenders', read: READ, share: WRITE }],
  workflows: [
    {
      entity: 'tenders.tender',
      read: READ,
      path: '/tenders/tenders/$id',
      initial: 'review',
      states: [
        { key: 'review', label: 'tenders.states.review', tone: 'info' },
        { key: 'preparing', label: 'tenders.states.preparing', tone: 'info' },
        { key: 'submitted', label: 'tenders.states.submitted', tone: 'warning' },
        { key: 'waiting', label: 'tenders.states.waiting', tone: 'warning' },
        { key: 'won', label: 'tenders.states.won', tone: 'success', final: true },
        { key: 'lost', label: 'tenders.states.lost', tone: 'neutral', final: true },
        { key: 'dropped', label: 'tenders.states.dropped', tone: 'neutral', final: true },
      ],
      transitions: [
        {
          key: 'prepare',
          label: 'tenders.transitions.prepare',
          from: ['review'],
          to: 'preparing',
          permission: WRITE,
        },
        {
          key: 'submit',
          label: 'tenders.transitions.submit',
          from: ['review', 'preparing'],
          to: 'submitted',
          permission: WRITE,
        },
        {
          key: 'wait',
          label: 'tenders.transitions.wait',
          from: ['submitted'],
          to: 'waiting',
          permission: WRITE,
          reason: 'optional',
        },
        {
          key: 'win',
          label: 'tenders.transitions.win',
          from: ['submitted', 'waiting'],
          to: 'won',
          permission: WRITE,
        },
        {
          key: 'lose',
          label: 'tenders.transitions.lose',
          from: ['submitted', 'waiting'],
          to: 'lost',
          permission: WRITE,
          reason: 'optional',
        },
        {
          key: 'drop',
          label: 'tenders.transitions.drop',
          from: ['review', 'preparing'],
          to: 'dropped',
          permission: WRITE,
          reason: 'optional',
        },
      ],
    },
  ],
  ai: [
    {
      // Summary and documents needed, from the details of the notice. Notices describe the
      // procurement; contact persons are not stored.
      key: 'tenders.summary',
      label: 'tenders.ai.summary',
      data: 'none',
      entities: ['tenders.tender'],
      permission: 'tenders.tender.summarize',
    },
    {
      // Second stage: also the tender documents. They may name contact persons, so the kernel
      // pseudonymizes them.
      key: 'tenders.summary-files',
      label: 'tenders.ai.summaryFiles',
      data: 'pseudonymized',
      entities: ['tenders.tender'],
      permission: 'tenders.tender.summarize',
      files: true,
    },
  ],
  events: { publishes: [tenderMarked.type, tenderStatusChanged.type] },
  dataProcessing: [
    {
      entity: 'tenders.tender',
      purpose:
        'Bearbeitung öffentlicher Ausschreibungen: verantwortliche und beteiligte Personen, Kommentare, Erinnerungen und Unterlagen einer Bewerbung',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // Notices name buyers, not people; the module keeps no contact persons from them.
      categories: ['account-id', 'free-text', 'file-name'],
      // Three years after the procedure ended, the regular limitation period (§ 195 BGB). A won
      // tender lives on in its project.
      retention: { period: 'P3Y', after: 'procedure-ended' },
      erase: 'delete',
      // Microsoft with the company's SharePoint as document store, if connected.
      processors: ['ai-provider', 'microsoft'],
    },
    {
      entity: 'tenders.item',
      purpose: 'Unterlagen zu einem Punkt der Abgabe-Checkliste, etwa ausgefüllte Formblätter',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      categories: ['account-id', 'file-name'],
      retention: { period: 'P3Y', after: 'procedure-ended' },
      erase: 'delete',
    },
    {
      entity: 'tenders.evidence',
      purpose:
        'Nachweise für Bewerbungen, z. B. Versicherungen und Zertifikate, mit der Person, die sie aktuell hält',
      legalBasis: 'Art. 6 Abs. 1 lit. f DSGVO',
      categories: ['account-id', 'file-name', 'free-text'],
      retention: { period: 'P1Y', after: 'expired' },
      erase: 'delete',
    },
  ],
});
